import React, { useState, useEffect, useCallback } from 'react';
import UsageEventsApi from '../../services/api/UsageEventsApi';
import { AnalyticsApi } from '../../services/api/AnalyticsApi';
import { BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import OnboardingAnalyticsTab from './OnboardingAnalyticsTab';
import './AcquisitionDashboard.css';

const AcquisitionDashboard = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('acquisition');
  const [dateRange, setDateRange] = useState(() => {
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - 30); // Last 30 days
    return { 
      from: from.toISOString().split('T')[0], 
      to: to.toISOString().split('T')[0] 
    };
  });

  // State for different data sections
  const [trafficSources, setTrafficSources] = useState([]);
  const [utmCampaigns, setUtmCampaigns] = useState([]);
  const [conversionFunnel, setConversionFunnel] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [anonymousStats, setAnonymousStats] = useState(null);

  // Fetch all acquisition data
  const fetchAcquisitionData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const fromDate = new Date(dateRange.from).toISOString();
      const toDate = new Date(dateRange.to + 'T23:59:59').toISOString();

      const [pageViewEvents, allEvents, funnelData] = await Promise.all([
        fetchEvents({ eventType: 'PAGE_VIEW', from: fromDate, to: toDate, limit: 10000 }),
        fetchEvents({ from: fromDate, to: toDate, limit: 10000 }),
        fetchAnonymousConversionFunnel(fromDate, toDate)
      ]);

      const trafficData = processTrafficSources(pageViewEvents);
      setTrafficSources(trafficData);

      const campaignData = processUTMCampaigns(pageViewEvents);
      setUtmCampaigns(campaignData);

      setConversionFunnel(funnelData);

      const pagesData = processTopLandingPages(pageViewEvents);
      setTopPages(pagesData);

      const anonStats = processAnonymousStats(allEvents);
      setAnonymousStats(anonStats);

    } catch (err) {
      console.error('Failed to fetch acquisition data:', err);
      setError(err.message || 'Failed to load acquisition analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  const fetchEvents = async ({ eventType, from, to, limit = 10000 }) => {
    const pageSize = Math.min(limit, 1000);
    let page = 0;
    let totalPages = 1;
    const collected = [];

    while (page < totalPages && collected.length < limit) {
      const response = await UsageEventsApi.adminSearch({
        eventType,
        from,
        to,
        page,
        size: pageSize
      });

      if (!response.ok) {
        throw new Error(`Usage events search failed: ${response.status}`);
      }

      const data = await response.json();
      const content = Array.isArray(data) ? data : (data.content || data.items || data.results || []);
      collected.push(...content);

      totalPages = Number.isInteger(data.totalPages) ? data.totalPages : (content.length < pageSize ? page + 1 : page + 2);
      page += 1;
    }

    return collected.slice(0, limit);
  };

  // Helper: Process traffic sources from already-fetched events
  const processTrafficSources = (events) => {
    const sourceMap = new Map();
    
    events.forEach(event => {
      const payload = event.payload || {};
      const source = payload.utm_source || payload.referrer || 'direct';
      
      if (!sourceMap.has(source)) {
        sourceMap.set(source, { source, visits: 0, users: new Set() });
      }
      
      const entry = sourceMap.get(source);
      entry.visits++;
      if (event.userId) entry.users.add(event.userId);
      if (event.sessionId) entry.users.add(event.sessionId);
    });

    return Array.from(sourceMap.values())
      .map(item => ({ ...item, users: item.users.size }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 10);
  };

  // Helper: Process UTM campaign performance from already-fetched events
  const processUTMCampaigns = (events) => {
    const campaignMap = new Map();
    
    events.forEach(event => {
      const payload = event.payload || {};
      if (!payload.utm_campaign) return;
      
      const key = `${payload.utm_source || 'unknown'}_${payload.utm_medium || 'unknown'}_${payload.utm_campaign}`;
      
      if (!campaignMap.has(key)) {
        campaignMap.set(key, {
          source: payload.utm_source || 'unknown',
          medium: payload.utm_medium || 'unknown',
          campaign: payload.utm_campaign,
          visits: 0,
          users: new Set()
        });
      }
      
      const entry = campaignMap.get(key);
      entry.visits++;
      if (event.userId) entry.users.add(event.userId);
      if (event.sessionId) entry.users.add(event.sessionId);
    });

    return Array.from(campaignMap.values())
      .map(item => ({ ...item, users: item.users.size }))
      .sort((a, b) => b.visits - a.visits)
      .slice(0, 10);
  };

  // Helper: Fetch anonymous to authenticated conversion funnel
  const fetchAnonymousConversionFunnel = async (from, to) => {
    try {
      const response = await AnalyticsApi.analyzeFunnel({
        steps: [
          { eventType: 'PAGE_VIEW' },
          { eventType: 'REGISTRATION_ATTEMPT' },
          { eventType: 'LOGIN_SUCCESS' }
        ],
        sessionBased: false,
        from,
        to
      });

      return response;
    } catch (err) {
      console.error('Failed to fetch conversion funnel:', err);
      return null;
    }
  };

  // Helper: Process top landing pages from already-fetched events
  const processTopLandingPages = (events) => {
    const pageMap = new Map();
    
    events.forEach(event => {
      const payload = event.payload || {};
      const page = payload.page || payload.url || 'unknown';
      
      if (!pageMap.has(page)) {
        pageMap.set(page, { page, views: 0, users: new Set() });
      }
      
      const entry = pageMap.get(page);
      entry.views++;
      if (event.userId) entry.users.add(event.userId);
      if (event.sessionId) entry.users.add(event.sessionId);
    });

    return Array.from(pageMap.values())
      .map(item => ({ ...item, users: item.users.size }))
      .sort((a, b) => b.views - a.views)
      .slice(0, 10);
  };

  // Helper: Process anonymous user statistics from already-fetched events
  const processAnonymousStats = (allEvents) => {
    if (!allEvents || allEvents.length === 0) return null;

    const anonymousSessions = new Set();
    const authenticatedUsers = new Set();
    let anonymousEvents = 0;
    let authenticatedEvents = 0;

    allEvents.forEach(event => {
      if (event.userId) {
        authenticatedUsers.add(event.userId);
        authenticatedEvents++;
      } else if (event.sessionId) {
        anonymousSessions.add(event.sessionId);
        anonymousEvents++;
      }
    });

    const totalSessions = anonymousSessions.size + authenticatedUsers.size;

    return {
      anonymousSessions: anonymousSessions.size,
      authenticatedUsers: authenticatedUsers.size,
      anonymousEvents,
      authenticatedEvents,
      totalSessions
    };
  };

  // Auto-fetch on mount and date range change
  useEffect(() => {
    fetchAcquisitionData();
  }, [fetchAcquisitionData]);

  // Chart colors
  const CHART_COLORS = ['#4a90e2', '#50c878', '#f5a623', '#e94b3c', '#9b59b6', '#1abc9c', '#e74c3c', '#3498db', '#f39c12', '#2ecc71'];

  return (
    <div className="acquisition-dashboard">
      <div className="dashboard-header">
        <h1>Acquisition Analytics</h1>
        <p className="dashboard-subtitle">
          Track how users discover your platform and analyze conversion from anonymous to authenticated
        </p>
      </div>

      {/* Tab Navigation */}
      <div className="acq-tab-bar">
        <button
          className={`acq-tab-btn${activeTab === 'acquisition' ? ' acq-tab-active' : ''}`}
          onClick={() => setActiveTab('acquisition')}
        >
          Acquisition
        </button>
        <button
          className={`acq-tab-btn${activeTab === 'onboarding' ? ' acq-tab-active' : ''}`}
          onClick={() => setActiveTab('onboarding')}
        >
          Onboarding
        </button>
      </div>

      {/* Date Range Controls */}
      <div className="date-range-controls">
        <div className="date-field">
          <label>From</label>
          <input 
            type="date" 
            value={dateRange.from}
            onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
          />
        </div>
        <div className="date-field">
          <label>To</label>
          <input 
            type="date" 
            value={dateRange.to}
            onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
          />
        </div>
        <button onClick={fetchAcquisitionData} disabled={loading} className="refresh-btn">
          {loading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {error && activeTab === 'acquisition' && <div className="error-message">{error}</div>}

      {loading && activeTab === 'acquisition' && <div className="loading-spinner">Loading acquisition data...</div>}

      {activeTab === 'onboarding' && (
        <OnboardingAnalyticsTab dateRange={dateRange} />
      )}

      {activeTab === 'acquisition' && !loading && (
        <>
          {/* Anonymous vs Authenticated Overview */}
          {anonymousStats && (
            <div className="stats-overview">
              <div className="stat-card">
                <div className="stat-label">Total Sessions</div>
                <div className="stat-value">{anonymousStats.totalSessions.toLocaleString()}</div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Anonymous Sessions</div>
                <div className="stat-value">{anonymousStats.anonymousSessions.toLocaleString()}</div>
                <div className="stat-subtitle">
                  {anonymousStats.totalSessions > 0 ? ((anonymousStats.anonymousSessions / anonymousStats.totalSessions) * 100).toFixed(1) : '0.0'}%
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Authenticated Users</div>
                <div className="stat-value">{anonymousStats.authenticatedUsers.toLocaleString()}</div>
                <div className="stat-subtitle">
                  {anonymousStats.totalSessions > 0 ? ((anonymousStats.authenticatedUsers / anonymousStats.totalSessions) * 100).toFixed(1) : '0.0'}%
                </div>
              </div>
              <div className="stat-card">
                <div className="stat-label">Total Events</div>
                <div className="stat-value">
                  {(anonymousStats.anonymousEvents + anonymousStats.authenticatedEvents).toLocaleString()}
                </div>
              </div>
            </div>
          )}

          {/* Anonymous to Authenticated Conversion Funnel */}
          {conversionFunnel && conversionFunnel.steps && conversionFunnel.steps.length > 0 && (
            <div className="section-card">
              <h2>Anonymous → Authenticated Conversion</h2>
              <div className="funnel-visualization">
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={conversionFunnel.steps} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis type="number" />
                    <YAxis dataKey="stepLabel" type="category" width={150} />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="usersEntered" fill="#4a90e2" name="Users Entered" />
                    <Bar dataKey="usersCompleted" fill="#50c878" name="Users Completed" />
                  </BarChart>
                </ResponsiveContainer>
                <div className="funnel-metrics">
                  <div className="metric-item">
                    <span className="metric-label">Overall Conversion:</span>
                    <span className="metric-value">{conversionFunnel.overallConversionRate?.toFixed(2)}%</span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-label">Total Entered:</span>
                    <span className="metric-value">{conversionFunnel.totalUsersEntered?.toLocaleString()}</span>
                  </div>
                  <div className="metric-item">
                    <span className="metric-label">Total Completed:</span>
                    <span className="metric-value">{conversionFunnel.totalUsersCompleted?.toLocaleString()}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Traffic Sources */}
          {trafficSources.length > 0 ? (
            <div className="section-card">
              <h2>Traffic Sources</h2>
              <div className="chart-grid">
                <div className="chart-container">
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={trafficSources}
                        dataKey="visits"
                        nameKey="source"
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        label={(entry) => `${entry.source}: ${entry.visits}`}
                      >
                        {trafficSources.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="data-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Source</th>
                        <th>Visits</th>
                        <th>Users</th>
                      </tr>
                    </thead>
                    <tbody>
                      {trafficSources.map((source, idx) => (
                        <tr key={idx}>
                          <td className="source-name">{source.source}</td>
                          <td>{source.visits.toLocaleString()}</td>
                          <td>{source.users.toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="section-card">
              <h2>Traffic Sources</h2>
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No traffic source data available for this period.</p>
            </div>
          )}

          {/* UTM Campaign Performance */}
          {utmCampaigns.length > 0 ? (
            <div className="section-card">
              <h2>Campaign Performance (UTM Tracking)</h2>
              <div className="data-table">
                <table>
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Medium</th>
                      <th>Campaign</th>
                      <th>Visits</th>
                      <th>Users</th>
                    </tr>
                  </thead>
                  <tbody>
                    {utmCampaigns.map((campaign, idx) => (
                      <tr key={idx}>
                        <td className="utm-source">{campaign.source}</td>
                        <td className="utm-medium">{campaign.medium}</td>
                        <td className="utm-campaign">{campaign.campaign}</td>
                        <td>{campaign.visits.toLocaleString()}</td>
                        <td>{campaign.users.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="section-card">
              <h2>Campaign Performance (UTM Tracking)</h2>
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No UTM campaign data found. Add utm_campaign parameters to your links to track campaigns.</p>
            </div>
          )}

          {/* Top Landing Pages */}
          {topPages.length > 0 ? (
            <div className="section-card">
              <h2>Top Landing Pages</h2>
              <div className="chart-container">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={topPages}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="page" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="views" fill="#4a90e2" name="Page Views" />
                    <Bar dataKey="users" fill="#50c878" name="Unique Users" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          ) : (
            <div className="section-card">
              <h2>Top Landing Pages</h2>
              <p style={{ color: '#6b7280', textAlign: 'center', padding: '2rem' }}>No landing page data available for this period.</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AcquisitionDashboard;
